import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import * as apiService from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intendedRoute, setIntendedRoute] = useState(null);

  // Function to save current route for page refresh persistence
  const saveCurrentRoute = (path) => {
    if (path && path !== '/login' && path !== '/') {
      sessionStorage.setItem('intendedRoute', path);
      setIntendedRoute(path);
    }
  };

  // Function to get and clear saved route
  const getAndClearIntendedRoute = () => {
    const savedRoute = sessionStorage.getItem('intendedRoute');
    if (savedRoute && savedRoute !== '/login' && savedRoute !== '/') {
      sessionStorage.removeItem('intendedRoute');
      setIntendedRoute(null);
      return savedRoute;
    }
    return null;
  };

  // Function to sync user data with the server
  const syncData = async () => {
    if (user && user._id) {
      try {
        // Get latest user data from DB
        const freshUserData = await apiService.getUserProfile();
        
        // Create a new object that preserves ONLY the auth token
        const updatedUser = {
          ...freshUserData,
          token: user.token,
          lastUpdated: Date.now()
        };
        
        
        // Update app state with fresh data - NO localStorage for user data
        setUser(updatedUser);
        
        // Only store auth token for session persistence, no user data
        const authInfo = {
          _id: updatedUser._id,
          token: updatedUser.token,
          lastUpdated: Date.now()
        };
        try {
          sessionStorage.setItem('auth_token', JSON.stringify(authInfo));
        } catch (storageErr) {
          console.warn('SessionStorage failed on mobile device:', storageErr.message);
          // Continue without storage - user will need to login again on refresh
        }
        
      } catch (err) {
        console.error('Error syncing user data:', err);
        // If we get a 429 rate limit error, skip future syncs for a while
        if (err.response && err.response.status === 429) {
          console.log('Rate limited - skipping sync for 30 minutes');
          // Don't throw error to prevent breaking the app
          return;
        }
      }
    }
  };

  useEffect(() => {
    // Check for saved route on page refresh
    const savedRoute = sessionStorage.getItem('intendedRoute');
    if (savedRoute && savedRoute !== '/login' && savedRoute !== '/') {
      setIntendedRoute(savedRoute);
    }

    // Check for auth token to maintain session across refreshes
    let storedAuth;
    try {
      storedAuth = sessionStorage.getItem('auth_token');
    } catch (storageErr) {
      console.warn('SessionStorage access failed on mobile:', storageErr.message);
      storedAuth = null;
    }
    
    const setupAuth = async (tokenInfo) => {
      // Set up auth headers if we have token info
      if (tokenInfo && tokenInfo.token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${tokenInfo.token}`;
      }
      
      if (tokenInfo && tokenInfo._id) {
        api.defaults.headers.common['X-User-Id'] = tokenInfo._id;
      }
      
      // Always fetch fresh user data from database on refresh
      if (tokenInfo && tokenInfo.token) {
        try {
          const freshUserData = await apiService.getUserProfile();
          
          // Use the fresh data from database with preserved token
          const updatedUser = {
            ...freshUserData,
            token: tokenInfo.token,
            lastUpdated: Date.now()
          };
          
          setUser(updatedUser);
          
          // Store minimal auth info for session persistence - NO user data
          const authInfo = {
            _id: updatedUser._id,
            token: tokenInfo.token,
            lastUpdated: Date.now()
          };
          try {
            sessionStorage.setItem('auth_token', JSON.stringify(authInfo));
          } catch (storageErr) {
            console.warn('SessionStorage failed on mobile device:', storageErr.message);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          // If we can't fetch fresh data, user needs to log in again
          createMockUser();
        }
      } else {
        // No valid token found, user needs to log in
        createMockUser();
      }
      
      setLoading(false);
    };
    
    if (storedAuth) {
      try {
        const parsedAuth = JSON.parse(storedAuth);
        
        // Only use stored data for auth token, always fetch user data from DB
        if (parsedAuth && parsedAuth.token) {
            setupAuth(parsedAuth);
        } else {
          console.log('No valid auth token found');
          createMockUser();
          setLoading(false);
        }
      } catch (err) {
        console.error('Error parsing stored auth', err);
        sessionStorage.removeItem('auth_token');
        localStorage.clear(); // Clear any old localStorage data
        createMockUser();
        setLoading(false);
      }
    } else {
      // No auth found, user needs to log in
      createMockUser();
      setLoading(false);
    }
    
    // Set up periodic data sync to get fresh DB data
    const syncInterval = setInterval(() => {
      syncData();
    }, 15 * 60 * 1000); // Every 15 minutes to reduce rate limiting (increased from 5 minutes)
    
    // Clean up interval on component unmount
    return () => {
      clearInterval(syncInterval);
    };
  }, []);
  
  // Create a mock user for offline/demo mode - NOW DISABLED
  const createMockUser = () => {
    // Instead of auto-creating a mock user, we'll now just set to null
    // This will force the user to actually log in
    setUser(null);
    console.log('No stored user found - login required');
  };

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      console.log('AuthContext login with:', { email });
      
      // Clear any existing session data
      sessionStorage.clear();
      localStorage.clear(); // Clear all localStorage
      
      // Use the real API service - pass email and password directly
      console.log('Calling apiService.login directly with credentials');
      const response = await apiService.login(email, password);
      
      console.log('Login response:', response);
      
      // Check if login was successful (response should have user data)
      if (!response || !response._id) {
        throw new Error('Invalid login response');
      }
      
      // Set user state with fresh data from login
      setUser(response);
      
      // Store only auth info for session persistence - NO user data
      const authInfo = {
        _id: response._id,
        token: response.token,
        lastUpdated: Date.now()
      };
      try {
        sessionStorage.setItem('auth_token', JSON.stringify(authInfo));
        console.log('User logged in, auth token stored for session persistence');
      } catch (storageErr) {
        console.warn('SessionStorage failed on mobile device:', storageErr.message);
        // Continue login process even if storage fails
      }
      
      // Set the Authorization header for future requests
      if (response && response.token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;
      }
      
      // Also set the X-User-Id header for profile requests
      if (response && response._id) {
        api.defaults.headers.common['X-User-Id'] = response._id;
      }
      
      // Skip immediate sync after login since we already have fresh data from login response
      // setTimeout(async () => {
      //   try {
      //     await syncData();
      //     console.log('Initial data sync completed after login');
      //   } catch (syncErr) {
      //     console.error('Error during initial sync:', syncErr);
      //   }
      // }, 1000);
      
      setLoading(false);
      return response;
    } catch (err) {
      console.error('Login error in context:', err);
      
      // Get a more meaningful error message from the API response if available
      let errorMessage = 'Login failed - check server logs for details';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Make sure to clear any partial auth data
      localStorage.clear(); // Clear all localStorage
      sessionStorage.clear();
      
      throw err;
    }
  };

  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      // Clear any existing session data
      localStorage.clear(); // Clear all localStorage
      sessionStorage.clear();
      
      // Use the real API service
      const response = await apiService.register(userData);
      
      console.log('Registration successful:', response);
      
      // Registration now returns a success message instead of user data with token
      // User must verify email before they can login
      setLoading(false);
      return response;
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed - check server logs for details');
      setLoading(false);
      throw err;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.clear(); // Clear all localStorage
    sessionStorage.clear();
    delete api.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['X-User-Id'];
    
    // Force page reload to clear any in-memory state
    window.location.href = '/login';
  };

  // Function to update the user data and optionally trigger a sync
  const updateUser = (userData, skipSync = false) => {
    if (user) {
      // Create a temporary update for UI responsiveness
      const updatedUser = { 
        ...user, 
        ...userData,
        lastUpdated: Date.now()
      };
      
      // Only update the React state (no localStorage persistence)
      setUser(updatedUser);
      
      // Only sync if not skipped to prevent cascading updates
      if (!skipSync) {
        syncData();
      }
    }
  };
  
  // Function to check if user is currently in jail
  const isInJail = () => {
    if (!user || !user.jailStatus) return false;
    
    const now = new Date();
    const jailEnd = new Date(user.jailStatus.jailEndTime);
    
    return user.jailStatus.isInJail && now < jailEnd;
  };

  // Function to get jail time remaining in minutes
  const getJailTimeRemaining = () => {
    if (!isInJail()) return 0;
    
    const now = new Date();
    const jailEnd = new Date(user.jailStatus.jailEndTime);
    
    return Math.max(0, Math.ceil((jailEnd - now) / (1000 * 60)));
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register,
      logout, 
      updateUser,
      syncData,
      isAuthenticated, 
      loading, 
      error,
      saveCurrentRoute,
      getAndClearIntendedRoute,
      intendedRoute,
      isInJail,
      getJailTimeRemaining
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);